import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { BrandMark } from "@/components/site/BrandMark";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin/enquiries" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/enquiries");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--nr-bg)] flex flex-col">
      <div className="px-6 lg:px-10 py-6">
        <Link
          to="/"
          data-testid="admin-login-back"
          className="inline-flex items-center gap-2 text-sm text-[color:var(--nr-ink-soft)] hover:text-[color:var(--nr-ink)]"
        >
          <ArrowLeft size={16} /> Back to site
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center mb-10">
            <BrandMark className="w-14 h-14 text-[color:var(--nr-accent)] mb-4" />
            <div className="font-serif-display text-2xl text-[color:var(--nr-ink)] leading-none mb-5">
              noah<span className="text-[color:var(--nr-accent)]">+</span>rayn
            </div>
            <p className="overline mb-3">Admin · Private area</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[color:var(--nr-ink)] leading-tight tracking-tight">
              Welcome back.
            </h1>
            <p className="mt-3 text-[color:var(--nr-ink-soft)] text-sm">
              Sign in to view incoming enquiries.
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="admin-login-form"
            className="bg-[color:var(--nr-bg-soft)] border border-[color:var(--nr-line)] rounded-2xl p-8 space-y-6"
          >
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)] flex items-center gap-2">
                <Mail size={12} /> Email
              </label>
              <input
                data-testid="admin-login-email"
                type="email"
                className="nr-input"
                placeholder="you@noahandrayn.com.sg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)] flex items-center gap-2">
                <Lock size={12} /> Password
              </label>
              <input
                data-testid="admin-login-password"
                type="password"
                className="nr-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-[#A33A1F] bg-[#FBE9E0] border border-[#E8C0AE] rounded-lg px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="btn-primary w-full rounded-full px-6 py-4 text-sm font-semibold tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-[color:var(--nr-ink-soft)]">
            Authorised personnel only. All sessions are logged.
          </p>
        </div>
      </div>
    </div>
  );
}
